import isEmpty from 'lodash/isEmpty';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { GenericObj } from '@solvhealth/types/interfaces/generics';
import { usePhotoIdUpload } from '~/components/PhotoIdUpload/util/usePhotoIdUpload';
import { DAPI_HOST } from '../../../config';
import { apiGetJson } from '../../../core/dapi';
import { getImageUrlDownloadUrl } from '~/core/dapi/images';
import { sendDataToState } from '~/ducks/paperwork';
import { ImageType } from '~/sagas/images';
import { getImageDataUrl } from '../util/getImageDataUrl';
import { ImageResponse } from './useImageUrl';

interface PaperworkAutofillResponse {
  paperwork_responses: { [index: string]: string };
  id_photos?: {
    photo_id_front_id?: string;
    photo_id_back_id?: string;
  };
}

export interface PaperworkAutofillData {
  paperworkResponses: { [index: string]: string };
  photoIdFront: {
    url?: string;
    id?: string;
  };
  photoIdBack: {
    url?: string;
    id?: string;
  };
}

export const useAutofillPaperwork = (bookingId: string): PaperworkAutofillData | undefined => {
  const [isAutofillInitialized, setIsAutofillInitialized] = useState(false);
  const { front, back, copyPhotoIdToState } = usePhotoIdUpload();
  const dispatch = useDispatch();
  //Get paperwork responses, send them to state as a part of formData to be used to autopopulate paperwork fields
  const data = useQuery(
    ['paperwork-responses', bookingId],
    async () => {
      if (!bookingId || isAutofillInitialized) {
        return null;
      }
      const response = await apiGetJson<PaperworkAutofillResponse>(
        `${DAPI_HOST}/v1/paperwork/${bookingId}/autofill`
      );
      if (!response?.data) {
        return null;
      }
      let frontDataUrl;
      let backDataUrl;
      let frontId;
      let backId;
      if (!isEmpty(response.data.id_photos) && !front.uploaded && !back.uploaded) {
        let idFrontResponse;
        let idBackResponse;
        frontId = response.data.id_photos?.photo_id_front_id || '';
        if (frontId) {
          idFrontResponse = await apiGetJson<ImageResponse>(getImageUrlDownloadUrl(frontId));
        }
        backId = response.data.id_photos?.photo_id_back_id || '';
        if (backId) {
          idBackResponse = await apiGetJson<ImageResponse>(getImageUrlDownloadUrl(backId));
        }
        if (response?.data?.id_photos?.photo_id_front_id && idFrontResponse?.data) {
          frontDataUrl = await getImageDataUrl(idFrontResponse.data.image_url);
        }
        if (response?.data?.id_photos?.photo_id_back_id && idBackResponse?.data) {
          backDataUrl = await getImageDataUrl(idBackResponse.data.image_url);
        }
      }
      return {
        paperworkResponses: response.data.paperwork_responses,
        photoIdFront: {
          url: frontDataUrl,
          id: frontId,
        },
        photoIdBack: {
          url: backDataUrl,
          id: backId,
        },
      };
    },
    {
      onSuccess: (data) => {
        if (data?.paperworkResponses) {
          dispatch(sendDataToState(data.paperworkResponses));
          setIsAutofillInitialized(true);
        }
        if (data?.photoIdFront?.url && data?.photoIdFront?.id) {
          const frontParams = {
            imageType: ImageType.PhotoIdFront,
            clear: false,
            cardId: data.photoIdFront.id,
            imageUrl: data.photoIdFront.url,
          };
          copyPhotoIdToState(frontParams);
        }
        if (data?.photoIdBack?.url && data?.photoIdBack?.id) {
          const backParams = {
            imageType: ImageType.PhotoIdBack,
            clear: false,
            cardId: data.photoIdBack.id,
            imageUrl: data.photoIdBack.url,
          };
          copyPhotoIdToState(backParams);
        }
        return data;
      },
      staleTime: 20 * 60 * 1000,
    }
  );
  return data.data ?? undefined;
};
